import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Typography, Grid, CircularProgress, Alert, Container,
  Stack, Chip, Collapse, Divider, Button, Pagination, Link, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import { downloadProjectPDF } from './generateProjectsPDF';
import { OrganizationsupdateCompanyProject, Organizationsprojectsfeedback } from "../../../helper/base_url";

const ITEMS_PER_PAGE = 3;

const getStatusColor = (status) => {
  switch (status) {
    case 'Active': return '#4caf50';
    case 'Pending': return '#ff9800';
    case 'Closed': return '#f44336';
    default: return '#757575';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'Active': return <CheckCircleIcon fontSize="small" />;
    case 'Pending': return <HourglassEmptyIcon fontSize="small" />;
    case 'Closed': return <CancelIcon fontSize="small" />;
    default: return null;
  }
};

const CompletedProjects = () => {
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [expandedProjects, setExpandedProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchProjects = async () => {
      const token = localStorage.getItem('company_token');
      try {
        const response = await axios.get(`${OrganizationsupdateCompanyProject}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const activeProjects = response.data.data.filter((p) => p.status === 'Completed');
        setProjects(activeProjects);
      } catch (err) {
        setError(err.response?.data?.error || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const handlePageChange = (_, value) => setPage(value);

  const toggleExpand = (id) => {
    setExpandedProjects((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const handleDownloadPDF = (project) => downloadProjectPDF(project);

  const handleSubmitFeedback = async (projectId, feedback) => {
    const token = localStorage.getItem('company_token');
    try {
      await axios.put(
        `${Organizationsprojectsfeedback}/${projectId}`,
        { feedback },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSnackbarOpen(true);
      setSelectedProjectId(null);
      setFeedbackText('');
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Something went wrong!');
      setErrorSnackbarOpen(true);
    }
  };

  const paginatedProjects = projects.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  if (loading)
    return (
      <Container sx={{ textAlign: 'center', marginTop : 8 }}>
        <CircularProgress />
      </Container>
    );

  if (error)
    return (
      <Box textAlign="center" paddingTop={10} mt={6}>
        <Typography variant="h6" color="textSecondary">
          No Completed Projects yet.
        </Typography>
      </Box>
    );

  return (
    <Box sx={{ width : '100%', backgroundColor: '#f4f6f8', padding: "80px 20px 20px 20px", height : '100%', overflowY: 'auto' }}>
      <Container>
        <Typography variant="h6" gutterBottom textAlign="center" fontWeight={400}>
          My Completed Projects
        </Typography>

        {projects.length === 0 ? (
          <Typography variant="body1" color="text.secondary" textAlign="center" mt={4}>
            No Completed projects found.
          </Typography>
        ) : (
          <>
            <Grid container spacing={4}>
              {paginatedProjects.map((project) => (
                <Grid item xs={12} md={6} key={project._id}>
                  <Card elevation={4} sx={{
                    position: 'relative', height : '100%', p: 2, borderRadius: 3,
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 25px rgba(0,0,0,0.15)' },
                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between'
                  }}>
                    <Stack direction="row" marginBottom="20px">
                      <Chip
                        icon={getStatusIcon(project.status)}
                        label={project.status}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top : 16,
                          right : 16,
                          fontWeight: 700,
                          color: 'white',
                          backgroundColor: getStatusColor(project.status),
                          textTransform: 'uppercase',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                        }}
                      />
                    </Stack>
                    <CardContent>
                      <Typography variant="body" fontWeight={400}>
                        <strong>Projects Title: </strong><span>{project.projectTitle}</span>
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1} mb={1} mt={1}>
                        <WorkIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                          {project.companyName}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" mb={2} color="text.secondary" noWrap>
                        {project.projectSummary}
                      </Typography>

                      <Stack direction="row" flexWrap="wrap" spacing={1} mb={2}>
                        {project.domain && (
                          <Chip label={project.domain} color="primary" variant="outlined" />
                        )}
                        {project.preferredTechStack && (
                          <Chip label={project.preferredTechStack} color="secondary" variant="outlined" />
                        )}
                        {project.preferredProgrammingLanguages && (
                          <Chip label={project.preferredProgrammingLanguages} variant="outlined" />
                        )}
                      </Stack>

                      <Collapse in={expandedProjects.includes(project._id)} timeout="auto" unmountOnExit>
                        <Divider sx={{ mb: 2 }} />

                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="body2"><strong>Deliverables:</strong> {project.expectedDeliverables}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2"><strong>Duration:</strong> {project.projectDuration}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2"><strong>Time/Week:</strong> {project.weeklyTimeCommitment || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2"><strong>Mode:</strong> {project.modeOfWork}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2"><strong>Compensation:</strong> {project.compensation || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2"><strong>Confidentiality:</strong> {project.confidentialityOrIPClauses || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6} display="flex" alignItems="center" gap={0.5}>
                            <CalendarTodayIcon fontSize="small" />
                            <Typography variant="body2">
                              <strong>Start:</strong>{' '}
                              {project.desiredStartDate
                                ? new Date(project.desiredStartDate).toLocaleDateString()
                                : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} display="flex" alignItems="center" gap={0.5}>
                            <CalendarTodayIcon fontSize="small" />
                            <Typography variant="body2">
                              <strong>Deadline:</strong>{' '}
                              {project.applicationDeadline
                                ? new Date(project.applicationDeadline).toLocaleDateString()
                                : 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          Point of Contact
                        </Typography>
                        <Typography variant="body2">{project.pointOfContact?.name || 'N/A'} - {project.pointOfContact?.designation || 'N/A'}</Typography>
                        <Typography variant="body2">Email: {project.pointOfContact?.email || 'N/A'}</Typography>
                        <Typography variant="body2">Phone: {project.pointOfContact?.phoneNumber || 'N/A'}</Typography>

                        {project.s3Url && (
                          <Typography variant="body2" mt={2}>
                            <strong>📎 Attachment: </strong>
                            <Link href={project.s3Url} target="_blank" rel="noopener" underline="hover">
                              Download
                            </Link>
                          </Typography>
                        )}
                      </Collapse>
                    </CardContent>

                    <Stack direction="row" justifyContent="space-between" px={2} pb={2}>
                      <Button variant="text" size="small" onClick={() => toggleExpand(project._id)}>
                        {expandedProjects.includes(project._id) ? 'View Less' : 'View More'}
                      </Button>
                      <Button variant="outlined" size="small" onClick={() => handleDownloadPDF(project)}>
                        Download PDF
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        onClick={() => {
                          setSelectedProjectId(project._id);
                          setFeedbackText(project.feedback || '');
                        }}
                      >
                        Give Feedback
                      </Button>
                    </Stack>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box mt={6} display="flex" justifyContent="center">
              <Pagination
                count={Math.ceil(projects.length / ITEMS_PER_PAGE)}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}

        {/* Feedback Dialog */}
        <Dialog
          open={!!selectedProjectId}
          onClose={() => setSelectedProjectId(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Submit Feedback for Project</DialogTitle>
          <DialogContent>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              rows={4}
              style={{
                width : '100%',
                padding: '10px',
                marginTop : '10px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                fontSize: '1rem',
              }}
              placeholder="Write your feedback here..."
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => handleSubmitFeedback(selectedProjectId, feedbackText)}
            >
              Submit Feedback
            </Button>
            <Button
              variant="text"
              color="error"
              onClick={() => setSelectedProjectId(null)}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
            Feedback submitted successfully!
          </Alert>
        </Snackbar>

        <Snackbar
          open={errorSnackbarOpen}
          autoHideDuration={3000}
          onClose={() => setErrorSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setErrorSnackbarOpen(false)}>
            {errorMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default CompletedProjects;
